import { useMemo, useRef } from "react";
import {
  CONTENT_HUB_SOURCES,
  useContentHubFeed,
  flattenContentHubPages,
  type ContentHubItem,
  type ContentHubSource,
  type FinancialReportsEUPayload,
  type FinXPayload,
  type StocksNewsPayload,
} from "@/features/content-hub";
import type {
  FinancialReportsEUStory,
  NewsFeedItem,
} from "@/shared/api/firehose/models";
import type { BackendSource } from "@/shared/api/firehose";

/** Envelope/state fields shared by every mapped story. */
const baseStoryFields = (item: ContentHubItem) => ({
  backend_source: item.backendSource as BackendSource,
  backend_source_id: 0,
  isNew: item.receivedAt !== undefined && !item.wasUpdated,
  received_at: item.receivedAt,
});

/** stocks-one/two/three/four(-filings) — news stories from The Fly,
 *  Benzinga & TradeXchange. Payload mirrors the legacy story model. */
const mapStocksNewsStory = (item: ContentHubItem): NewsFeedItem => {
  const payload = (item.raw ?? {}) as Partial<StocksNewsPayload>;
  return {
    ...baseStoryFields(item),
    id: payload.id ?? Number(item.sourceId),
    title: item.title,
    body: item.body ?? undefined,
    url: payload.url,
    source: item.sourceType,
    source_attributable: payload.author,
    created: payload.created ?? item.occursAt,
    updated: payload.updated ?? payload.created ?? item.occursAt,
    created_utc: payload.created_utc ?? item.occursAt,
    tickers: item.tickers,
  };
};

/** fin-x / energy-x — posts from X (Twitter). Post text arrives as `text`. */
const mapFinXStory = (item: ContentHubItem): NewsFeedItem => {
  const payload = (item.raw ?? {}) as Partial<FinXPayload>;
  return {
    ...baseStoryFields(item),
    id: payload.id ?? Number(item.sourceId),
    title: item.title,
    url: payload.url,
    source: item.sourceType,
    source_attributable: payload.author_name
      ? `${payload.author_name} via X`
      : undefined,
    created: payload.created_at ?? item.occursAt,
    updated: payload.created_at ?? item.occursAt,
    created_utc: payload.created_utc ?? item.occursAt,
    tickers: item.tickers,
  };
};

/** stocks-five-eu — EU financial reports. Extends the news-story shape with
 *  filing metadata the report details pane renders. */
const mapEuReportStory = (item: ContentHubItem): FinancialReportsEUStory => {
  const payload = (item.raw ?? {}) as Partial<FinancialReportsEUPayload>;
  return {
    ...baseStoryFields(item),
    id: payload.id ?? Number(item.sourceId),
    title: item.title,
    body: item.body ?? undefined,
    url: payload.url,
    source: item.sourceType,
    source_attributable: payload.author,
    created: payload.created ?? item.occursAt,
    updated: payload.updated ?? payload.created ?? item.occursAt,
    created_utc: payload.created_utc ?? item.occursAt,
    tickers: item.tickers,
    // Filing metadata for FinancialReportsEUItemDetails.
    company_name: payload.company_name,
    country_code_alpha2: payload.country_code_alpha2,
    country_name: payload.country_name,
    filing_type_code: payload.filing_type_code,
    filing_type_name: payload.filing_type_name,
    language_code: payload.language_code,
    language_name: payload.language_name,
    document_url: payload.document_url,
    isins: payload.isins,
    lei: payload.lei,
    frep_filing_id: payload.frep_filing_id,
  };
};

/** Fallback for sources without a declared story mapper yet — built from
 *  the normalized item only. */
const mapDefaultStory = (item: ContentHubItem): NewsFeedItem => ({
  ...baseStoryFields(item),
  id: Number(item.sourceId),
  title: item.title,
  body: item.body ?? undefined,
  source: item.sourceType,
  created: item.occursAt,
  updated: item.occursAt,
  created_utc: item.occursAt,
  tickers: item.tickers,
});

/** Per-source story mappers. Add an entry when integrating a new firehose
 *  source (truth-social has its own widget-specific mapper). */
const storyMappers: Partial<
  Record<ContentHubSource, (item: ContentHubItem) => NewsFeedItem>
> = {
  [CONTENT_HUB_SOURCES.FIN_X]: mapFinXStory,
  [CONTENT_HUB_SOURCES.ENERGY_X]: mapFinXStory,
  [CONTENT_HUB_SOURCES.STOCKS_ONE]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_TWO]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_THREE]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_FILINGS]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_FEDSPEAK]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.STOCKS_FOUR_GENERAL]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.CRYPTO_WEB]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.FIN_WEB]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.ENERGY_WEB]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.SEC_FILINGS]: mapStocksNewsStory,
  [CONTENT_HUB_SOURCES.FINANCIAL_REPORTS_EU]: mapEuReportStory,
};

/** Map a normalized content-hub item into the legacy NewsFeedItem model the
 *  firehose row components render, using the source's declared schema. */
export const mapContentHubItemToStory = (item: ContentHubItem): NewsFeedItem =>
  (storyMappers[item.backendSource as ContentHubSource] ?? mapDefaultStory)(
    item
  );

/**
 * Content-hub-backed base feed for one or more firehose sources: history
 * from /v1/search, live stories from the shared /v1/stream connection,
 * adapted to the legacy NewsFeedItem model. The generic counterpart of the
 * legacy useFeed(fetchFn, sources) — a single-source widget passes one
 * source, a combined view passes a source group; both read the same
 * per-source caches, so mounting them together costs nothing extra.
 *
 * Pagination beyond the initial search page is a content-hub follow-up, so
 * `hasMore` is always false.
 */
export const useContentHubFirehoseFeed = (
  source: ContentHubSource | readonly ContentHubSource[],
  options?: { enabled?: boolean }
) => {
  const sources = useMemo(
    () => (typeof source === "string" ? [source] : source),
    [source]
  );
  // Never fire an all-sources (empty backend_source) query — a combined view
  // whose source list hasn't resolved yet (e.g. firehose-all before roles load)
  // stays disabled until it has at least one source.
  const enabled = (options?.enabled ?? true) && sources.length > 0;
  const query = useContentHubFeed(sources, { enabled });

  // Cache mapped stories per content-hub item so row object identity is
  // stable across stream patches (the row component mutates show_deatils on
  // the object; unstable identity would reset it and re-render all rows).
  const mappedCacheRef = useRef(new WeakMap<ContentHubItem, NewsFeedItem>());

  const stories = useMemo(() => {
    return flattenContentHubPages(query.data)
      .map((item): NewsFeedItem => {
        const cached = mappedCacheRef.current.get(item);
        if (cached) return cached;
        const story = mapContentHubItemToStory(item);
        mappedCacheRef.current.set(item, story);
        return story;
      })
      // Defensive sort, newest first — stream patches prepend by arrival
      // order, which can differ from published order.
      .sort(
        (a, b) =>
          Date.parse(b.created_utc || "") - Date.parse(a.created_utc || "")
      );
  }, [query.data]);

  return {
    stories,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    loadNext: query.fetchNextPage,
    isLoadingMore: query.isFetchingNextPage,
    hasMore: query.hasNextPage,
  };
};
